// `shareTradeDirect` — fire-and-forget share for the post-trade snackbar.
//
// The snackbar is ephemeral (5s lifetime), so we skip the full
// `<ShareModal>` flow and dispatch directly: try the native share sheet
// (mobile, when supported), fall back to opening an X tweet intent.
//
// Internal — components consume the public surface in `lib/share/index.ts`.

import { resolveCanonicalOrigin } from '@/lib/seo';

import { nativeShare, openShareIntent } from './share';
import { type BuildTradeShareUrlContext, buildTradeShareUrl } from './trade-url';

export interface ShareTradeDirectInput extends BuildTradeShareUrlContext {
  signature: string;
  /** Pre-composed share text. Already-localized at the call site. */
  text: string;
  /** Optional title for the native share sheet (Android/Win). Defaults
   *  to a generic "Morfi" if omitted. */
  title?: string;
}

/**
 * Try the platform native share sheet first; on `unsupported` / `error`,
 * fall back to an X tweet intent so the user always lands somewhere.
 * `aborted` (user dismissed the native sheet) is intentionally not
 * forwarded — re-opening X on top would feel hostile.
 *
 * Both paths emit `social_trade_shared` via `emitShareEvent` inside
 * the underlying primitives — never call `onEvent('social_trade_shared')`
 * from a UI component directly.
 */
export async function shareTradeDirect(input: ShareTradeDirectInput): Promise<void> {
  const url = new URL(
    buildTradeShareUrl(input.signature, {
      marketPubkey: input.marketPubkey,
      userPubkey: input.userPubkey,
      direction: input.direction,
      amountD6: input.amountD6,
      outcomeIndex: input.outcomeIndex,
    }),
    resolveCanonicalOrigin(),
  ).toString();

  const surface = {
    kind: 'trade' as const,
    signature: input.signature,
    marketPubkey: input.marketPubkey,
    userPubkey: input.userPubkey,
    direction: input.direction,
    amountD6: input.amountD6,
    outcomeIndex: input.outcomeIndex,
  };

  const nativePayload: Parameters<typeof nativeShare>[0] = {
    url,
    text: input.text,
    surface,
  };
  if (input.title !== undefined) nativePayload.title = input.title;
  const result = await nativeShare(nativePayload);
  if (result === 'shared' || result === 'aborted') return;

  openShareIntent('x', {
    url,
    text: input.text,
    surface,
  });
}
